package com.havn.app.features.reports.domain

data class SellerReport(
    val id: String,
    val openHouseId: String? = null,
    val propertyId: String? = null,
    val propertyTitle: String? = null,
    val propertyAddress: String? = null,
    val propertyThumbnailDataUrl: String? = null,
    val preparedByAgentId: String,
    val generatedByUserId: String? = null,
    val status: SellerReportStatus,
    val visitorCount: Int,
    val hotCount: Int,
    val warmCount: Int,
    val coldCount: Int,
    val averageIntentScore: Double? = null,
    val summary: String? = null,
    val sellerFeedback: String? = null,
    val nextSteps: String? = null,
    val sentAt: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null
)

data class SellerReportPreview(
    val reportId: String? = null,
    val openHouseId: String? = null,
    val propertyId: String? = null,
    val propertyTitle: String = "",
    val propertyAddress: String,
    val openHouseDateTime: String,
    val reportGeneratedAt: String = "",
    val propertyImageDataUrl: String? = null,
    val propertyPrice: String = "",
    val propertyStatus: String = "",
    val daysActive: Int = 0,
    val reportPeriod: String = "",
    val openHouseCount: Int = 0,
    val inquiryCount: Int = 0,
    val leadSources: Map<String, Int> = emptyMap(),
    val totalVisitors: Int,
    val interestedBuyers: Int = 0,
    val hotLeads: Int,
    val warmLeads: Int,
    val coldLeads: Int,
    val privateTours: Int = 0,
    val offers: Int = 0,
    val mostCommonBuyerInterest: String,
    val followUpsSent: Int,
    val callsMade: Int = 0,
    val smsSent: Int = 0,
    val emailsSent: Int = 0,
    val financingInfoShared: Int = 0,
    val reportsCount: Int = 0,
    val agentNotes: String,
    val summaryText: String,
    val reportTitle: String = "Seller Report",
    val healthScoreLabel: String = "",
    val buyerSentimentSummary: String = summaryText,
    val mostLovedFeatures: String = mostCommonBuyerInterest,
    val commonConcerns: String = "No consistent concerns have emerged from the available buyer feedback.",
    val leadQualitySummary: String = "",
    val agentActivitySummary: String = "",
    val recommendations: String = "",
    val nextSteps: String = "",
    val marketPositionComparison: String = "",
    val sellerConfidenceScore: Int = 0,
    val sellerConfidenceExplanation: String = "",
    val offerReadinessScore: Int = 0,
    val offerReadinessExplanation: String = "",
    val keyBuyerPatterns: String = "",
    val averageVisitDuration: String = "Not tracked",
    val returningBuyers: Int = 0,
    val timeline: List<PropertyReportOpenHouseInsight> = emptyList(),
    val featureInterest: List<PropertyReportFeatureInterest> = emptyList(),
    val concerns: List<PropertyReportFeatureInterest> = emptyList(),
    val aiInsightsJson: String = "",
    val status: SellerReportStatus = SellerReportStatus.Draft,
    val isAiDraft: Boolean = false
)

data class SellerReportAiInput(
    val language: String,
    val propertyTitle: String,
    val propertyAddress: String,
    val propertyCity: String = "",
    val propertyPrice: String,
    val propertyStatus: String,
    val daysOnMarket: Int,
    val reportPeriodStart: String,
    val reportPeriodEnd: String,
    val openHouses: List<SellerReportAiOpenHouse>,
    val totals: SellerReportAiTotals,
    val agentNotes: List<String>
)

data class SellerReportAiOpenHouse(
    val date: String,
    val visitors: Int,
    val hotLeads: Int,
    val privateTours: Int,
    val followUpsSent: Int
)

data class SellerReportAiTotals(
    val visitors: Int,
    val hotLeads: Int,
    val warmLeads: Int,
    val coldLeads: Int,
    val privateTours: Int,
    val followUpsSent: Int
    ,val propertyInquiries: Int = 0
    ,val leadSources: Map<String, Int> = emptyMap()
)

interface SellerReportAiService {
    suspend fun generateInsights(input: SellerReportAiInput): Result<String>
}

data class PropertyReportOpenHouseInsight(
    val id: String,
    val title: String,
    val date: String,
    val time: String,
    val visitors: Int,
    val interestedBuyers: Int,
    val hotLeads: Int,
    val privateTours: Int,
    val followUpsSent: Int,
    val sentimentScore: Int
)

data class PropertyReportFeatureInterest(
    val label: String,
    val score: Int
)

data class SellerReportDraft(
    val openHouseId: String,
    val visitorCount: Int,
    val hotCount: Int,
    val warmCount: Int,
    val coldCount: Int,
    val averageIntentScore: Double? = null,
    val summary: String,
    val sellerFeedback: String? = null,
    val nextSteps: String? = null,
    val status: SellerReportStatus = SellerReportStatus.Draft
)

enum class SellerReportStatus(val label: String, val databaseValue: String) {
    Draft("Draft", "draft"),
    Accepted("Accepted", "accepted"),
    Archived("Archived", "archived");

    companion object {
        fun fromDatabase(value: String?): SellerReportStatus = when (value) {
            "accepted", "ready", "sent" -> Accepted
            "archived" -> Archived
            else -> Draft
        }
    }
}
